import { useState } from 'react'

import { FvButton, Icon } from '@fv/client-components'
import type { LoadTemplateDTO, TemplateLocation, Workflow } from '@fv/models'

import { HelpHrefButton } from '@/components/HelpHrefButton'
import { InputGroup } from '@/components/inputs/InputGroup'
import { ShipperSlider } from '@/components/ShipperSlider'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import { useAccountBrand } from '@/features/company-branding/api'
import { useLoadTemplates } from '@/features/settings/shipment-templates/queries'
import { ShipmentTemplatePanel } from '@/features/settings/shipment-templates/ShipmentTemplatePanel'
import { useTypeNames } from '@/hooks/settings'
import { alphabeticalSorter } from '@/utils/alphabeticalSorter'

import { HasPermission } from '../auth'

type Props = {
  selectedTemplate?: LoadTemplateDTO | null
  disabled?: boolean
  workflow: Workflow
  onSelect: (template: LoadTemplateDTO) => void
  onClear?: () => void
}

type IncludedDetail = { label: string; values: string[] }

const formatLocation = (location: TemplateLocation) =>
  [
    location.company,
    location.address,
    [location.city, location.state].filter(Boolean).join(', '),
    location.postalCode,
  ]
    .filter(Boolean)
    .join(' ')

const useIncludedDetails = (
  template: LoadTemplateDTO | null,
): IncludedDetail[] => {
  const { equipmentName, packageName } = useTypeNames()
  const brand = useAccountBrand(template?.brandId)
  if (!template) return []

  const {
    origin,
    destination,
    equipment,
    items,
    brandId,
    billTo,
    glCode,
    tags,
  } = template

  const details: Array<IncludedDetail | false | undefined | ''> = [
    origin && { label: 'Origin', values: [formatLocation(origin)] },
    destination && {
      label: 'Destination',
      values: [formatLocation(destination)],
    },
    equipment?.type && {
      label: 'Equipment',
      values: [equipmentName(equipment.type)],
    },
    !!items?.length && {
      label: 'Items',
      values: items.map(item =>
        [
          item.quantity,
          item.type && packageName(item.type),
          item.description && `- ${item.description}`,
        ]
          .filter(Boolean)
          .join(' '),
      ),
    },
    brandId &&
      brand && {
        label: 'Custom brand',
        values: [brand.name],
      },
    billTo?.company && { label: 'Bill to', values: [billTo.company] },
    glCode && { label: 'GL code', values: [glCode] },
    !!tags?.length && { label: 'Tags', values: tags },
  ]

  return details.filter((d): d is IncludedDetail => !!d)
}

export const ShipmentTemplateSelector = ({
  selectedTemplate = null,
  disabled,
  workflow,
  onSelect,
  onClear,
}: Props) => {
  const [showSlider, setShowSlider] = useState(false)
  const [editingTemplate, setEditingTemplate] =
    useState<LoadTemplateDTO | null>(null)
  const { data: templates, isLoading } = useLoadTemplates()
  const workflowTemplates =
    templates
      ?.filter(template => template.workflow === workflow)
      .slice()
      .sort(alphabeticalSorter('name')) ?? []

  const handleChange = (templateId: string) => {
    if (!templateId) {
      onClear?.()
      return
    }

    const template = workflowTemplates.find(t => t._id === templateId)
    template && onSelect(template)
  }

  const includedDetails = useIncludedDetails(selectedTemplate)

  const closeSlider = () => {
    setShowSlider(false)
    setEditingTemplate(null)
  }

  return (
    <>
      <div className="border-t border-b border-dotted border-fv-blue-300 mt-3 mb-1 py-4 min-w-0 b1350:border-t-0 b1350:border-b-0 b1350:flex b1350:m-0 b1350:p-0 b1350:items-center b1350:border-l b1350:pl-3">
        <label className="mb-2 b1350:hidden flex items-center gap-x-1">
          Apply a quote template
          <HelpHrefButton href="https://blog.freightview.com/shipment-templates" />
        </label>

        <InputGroup
          className="mb-0 leading-none b1350:max-w-60"
          inputType="select"
          inputProps={{
            name: 'shipmentPreset',
            disabled: isLoading || disabled,
            value: selectedTemplate?._id ?? '',
            onChange: e => handleChange(e.target.value),
            options: [
              { text: 'No template applied', value: '' },
              ...workflowTemplates.map(template => ({
                text: template.name,
                value: template._id,
              })),
            ],
          }}
        />

        {includedDetails.length > 0 && (
          <div className="mt-4 mb-1 b1350:m-0 b1350:p-0">
            <div className="flex items-center my-2 fa-width-auto ml-1 b1350:hidden">
              <Icon icon="arrow-down" className="text-fv-gray-dark" />
              <p className="mb-0">Included in template:</p>
            </div>
            <ol className="list-[upper-alpha] list-inside gap-y-1 flex flex-col b1350:flex-row b1350:gap-x-2 b1350:ml-2">
              {includedDetails.map(detail => (
                <li
                  className="bg-fv-beer-xlight px-1 border border-fv-beer-medium rounded-xs relative b1350:pr-8"
                  key={detail.label}
                >
                  {detail.label}
                  <ShipperTooltip
                    label={
                      <ul className="list-none">
                        {detail.values.map((value, i) => (
                          <li key={i}>{value}</li>
                        ))}
                      </ul>
                    }
                  >
                    <FvButton
                      className="absolute top-1/2 -translate-y-1/2 right-1"
                      icon="info-circle"
                    />
                  </ShipperTooltip>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
      <HasPermission name="manage-settings">
        <FvButton
          className="self-start b1350:hidden"
          icon="plus"
          onClick={() => {
            setEditingTemplate(null)
            setShowSlider(true)
          }}
        >
          Add new template
        </FvButton>
      </HasPermission>

      <ShipperSlider
        isOpen={showSlider}
        onClose={closeSlider}
        width="60rem"
        flush
      >
        {showSlider && (
          <ShipmentTemplatePanel
            key={editingTemplate?._id ?? 'new'}
            existingTemplate={editingTemplate ?? undefined}
            initialWorkflow={workflow}
            onSaved={template => {
              if (!disabled && template.workflow === workflow) {
                onSelect(template)
              }
              closeSlider()
            }}
            onCancel={closeSlider}
          />
        )}
      </ShipperSlider>
    </>
  )
}
